import { Route, Routes } from 'react-router-dom';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { AuthLayout } from './components/AuthLayout';
import { X5Client } from './pages/X5Client';
import { Profile } from './pages/Profile';
import { Logout } from './pages/Logout';
import { NotFound } from './pages/NotFound';
import { useSupportWidget } from './lib/support';

/**
 * Split on auth state rather than nesting the app inside `<Authenticator>`.
 *
 * The render-prop form puts the signed-in app *inside* the Authenticator's own
 * container, which means any wrapper markup for the sign-in screen would also
 * wrap the portal. Reading `authStatus` from the shared provider instead lets
 * the signed-out screen own the whole viewport - which is what the split-panel
 * layout needs - while the routes render unwrapped once signed in.
 */
function Root() {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);

  // Above the early returns because it is a hook, and one place rather than the
  // two sign-out call sites — see useSupportWidget.
  useSupportWidget(authStatus === 'authenticated');

  // 'configuring' is the tick before Amplify has read stored tokens. Rendering
  // the sign-in panel here would flash it at users who already have a session.
  if (authStatus === 'configuring') return null;

  if (authStatus !== 'authenticated') return <AuthLayout />;

  // No wrapper element: each screen owns its own <main>, because the streaming
  // view is a full-height flex column and an extra block between it and the
  // body would collapse the height the embed sizes itself against.
  return (
    <Routes>
      <Route path="/" element={<X5Client />} />
      <Route path="/x5" element={<X5Client />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/logout" element={<Logout />} />
      {/* The version this replaces gave its 404 route no path, so it never
          matched and an unknown URL rendered nothing at all. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export function App() {
  return (
    <Authenticator.Provider>
      <Root />
    </Authenticator.Provider>
  );
}
